import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mic, X, Square, Play, Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SAMPLE_PARAGRAPH = `Once upon a time, in a quiet little village by the edge of a great forest, there lived a curious child who loved nothing more than to listen to stories. Every evening, when the lanterns were lit and the stars began to shine, the child would settle into a soft chair and ask, "Tell me a story." And so, every story began. Some stories made them laugh, some made them brave, and some made them dream of faraway places.`;

const MIN_SECONDS = 30;
const TARGET_SECONDS = 60;
const MAX_SECONDS = 180;

const CloneVoiceModal = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [level, setLevel] = useState(0); // 0..1, audio meter

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  // Cleanup on close
  useEffect(() => {
    if (!open) reset();
    return () => cleanupStreams();
    // eslint-disable-next-line
  }, [open]);

  // Lock body scroll while modal is open so background doesn't shift around
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const cleanupStreams = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
  };

  const reset = () => {
    cleanupStreams();
    setName('');
    setConsent(false);
    setRecording(false);
    setElapsed(0);
    setBlob(null);
    setBlobUrl(null);
    setSubmitting(false);
    setError(null);
    setLevel(0);
    chunksRef.current = [];
  };

  const startRecording = async () => {
    setError(null);
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError('Your browser does not support audio recording. Try Chrome, Edge, or Safari.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 44100, echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;

      // Live audio level meter so the parent knows the mic is picking up
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let max = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = Math.abs(buf[i] - 128) / 128;
          if (v > max) max = v;
        }
        setLevel(max);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      // Choose the best mime type the browser supports
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
      const mime = candidates.find((m) => MediaRecorder.isTypeSupported(m)) || '';
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const out = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        setBlob(out);
        setBlobUrl(URL.createObjectURL(out));
        cleanupStreams();
      };
      mr.start(250);
      setRecording(true);
      setElapsed(0);
      tickRef.current = setInterval(() => {
        setElapsed((s) => {
          if (s + 1 >= MAX_SECONDS) {
            stopRecording();
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      console.error('mic error', e);
      setError(e.name === 'NotAllowedError'
        ? 'Microphone permission was denied. Allow it in your browser and try again.'
        : `Could not start recording: ${e.message}`);
    }
  };

  const stopRecording = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim()) return setError('Give this voice a name (e.g. "Mom", "Dad").');
    if (!consent) return setError('Please confirm consent before cloning.');
    if (!blob) return setError('Record an audio sample first.');
    if (elapsed < MIN_SECONDS) return setError(`Sample is too short. Record at least ${MIN_SECONDS} seconds (you have ${elapsed}s).`);

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('consent_confirmed', 'true');
      const ext = (blob.type.includes('mp4') ? 'm4a' : (blob.type.includes('ogg') ? 'ogg' : 'webm'));
      fd.append('sample', blob, `sample.${ext}`);

      const token = localStorage.getItem('token');
      const r = await axios.post(`${BACKEND_URL}/api/voices/cloned`, fd, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 180_000,
      });
      toast.success(`Voice "${r.data.name}" cloned successfully!`);
      onCreated?.(r.data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || 'Unknown error';
      setError(`Could not clone voice: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const meterPct = Math.min(100, Math.round(level * 140));

  // Render at document.body via portal so the modal escapes any local
  // stacking context / overflow:hidden parent and reliably covers the page.
  // Without this, clicks can pass through to elements behind the panel and
  // the modal may be clipped inside a scroll container.
  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.7)', zIndex: 9999 }}
      data-testid="clone-voice-modal"
      onClick={(e) => {
        // Click on backdrop closes; click on the modal body doesn't bubble.
        // But don't auto-close while submitting or while recording (would lose work).
        if (e.target === e.currentTarget && !submitting && !recording) onClose?.();
      }}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-6 my-auto"
        style={{
          background: '#1a1815',
          border: '1px solid rgba(212,168,83,0.25)',
          maxHeight: 'calc(100vh - 2rem)',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Mic size={20} style={{ color: '#D4A853' }} />
            <h3 className="text-lg font-bold" style={{ color: '#f0e8d4' }}>Clone a Voice</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" disabled={submitting}>
            <X size={18} style={{ color: '#a39b87' }} />
          </button>
        </div>

        <p className="text-xs mb-4" style={{ color: '#a39b87' }}>
          Record a clean 60-second sample of someone's voice (yourself, a grandparent, etc.). That voice will then read your child's stories — in any of the 29 supported languages.
        </p>

        {/* Step 1: name */}
        <label className="block text-xs font-bold uppercase mb-1" style={{ color: '#D4A853' }}>Voice name</label>
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={submitting}
          placeholder='e.g. "Mom", "Dad", "Grandma Rose"'
          maxLength={40}
          className="w-full mb-4 px-3 py-2 rounded-md text-sm"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#f0e8d4' }}
          data-testid="clone-voice-name"
        />

        {/* Step 2: sample paragraph to read */}
        <label className="block text-xs font-bold uppercase mb-1" style={{ color: '#D4A853' }}>Read this aloud</label>
        <div className="p-3 mb-3 text-xs rounded-md leading-relaxed" style={{ background: 'rgba(212,168,83,0.06)', border: '1px solid rgba(212,168,83,0.15)', color: '#d4cdb8' }}>
          {SAMPLE_PARAGRAPH}
        </div>

        {/* Step 3: recorder */}
        <div className="flex items-center gap-3 mb-3">
          {!recording && !blob && (
            <button onClick={startRecording} disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-md font-bold text-sm disabled:opacity-50"
              style={{ background: '#D4A853', color: '#000' }}
              data-testid="clone-voice-record">
              <Mic size={16} /> Start Recording
            </button>
          )}
          {recording && (
            <button onClick={stopRecording}
              className="flex items-center gap-2 px-4 py-2 rounded-md font-bold text-sm"
              style={{ background: '#e11d48', color: '#fff' }}
              data-testid="clone-voice-stop">
              <Square size={14} /> Stop ({elapsed}s)
            </button>
          )}
          {blob && !recording && (
            <>
              <audio src={blobUrl} controls className="flex-1 h-8" />
              <button onClick={() => { setBlob(null); setBlobUrl(null); setElapsed(0); }}
                disabled={submitting}
                className="text-xs font-bold px-2 py-1 rounded" style={{ color: '#a39b87', border: '1px solid rgba(255,255,255,0.1)' }}>
                Re-record
              </button>
            </>
          )}
        </div>

        {/* Mic level meter while recording */}
        {recording && (
          <div className="mb-3">
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div className="h-full transition-all" style={{ width: `${meterPct}%`, background: meterPct > 8 ? '#10b981' : '#e11d48' }} />
            </div>
            <p className="text-[10px] mt-1" style={{ color: meterPct > 8 ? '#a3e9c8' : '#fda4af' }}>
              {meterPct > 8 ? 'Mic is picking you up' : 'Speak louder — mic level is very low'}
              {' · '}{elapsed}s {elapsed < MIN_SECONDS ? `(need ${MIN_SECONDS}s)` : elapsed < TARGET_SECONDS ? `(${TARGET_SECONDS}s ideal)` : '✓'}
            </p>
          </div>
        )}

        {/* Step 4: consent */}
        <label className="flex items-start gap-2 mb-3 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={submitting}
            className="mt-0.5"
            data-testid="clone-voice-consent" />
          <span className="text-xs leading-relaxed" style={{ color: '#d4cdb8' }}>
            I confirm I have permission from the person whose voice is in this recording to clone it for use inside Semantic Vision. I understand the voice can be deleted at any time.
          </span>
        </label>

        {error && (
          <div className="flex items-start gap-2 p-2 mb-3 rounded-md" style={{ background: 'rgba(225,29,72,0.1)', border: '1px solid rgba(225,29,72,0.3)' }}>
            <AlertCircle size={14} style={{ color: '#fda4af', marginTop: 2 }} />
            <span className="text-xs" style={{ color: '#fda4af' }}>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} disabled={submitting}
            className="px-4 py-2 rounded-md text-sm font-bold disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.04)', color: '#d4cdb8', border: '1px solid rgba(255,255,255,0.1)' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting || !blob || !consent || !name.trim() || elapsed < MIN_SECONDS}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold disabled:opacity-50"
            style={{ background: '#D4A853', color: '#000' }}
            data-testid="clone-voice-submit">
            {submitting ? (<><Loader2 size={14} className="animate-spin" /> Cloning… (15-45s)</>) : (<><Upload size={14} /> Clone Voice</>)}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default CloneVoiceModal;
